import React, { useEffect, useMemo, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from "recharts";
import api from "../lib/api";
import { getStoredUser } from "../lib/auth";
import { useLanguage } from "../lib/language";
import { repairMojibakeDeep } from "../lib/text";

const PIE_COLORS = ["#1f7a8c", "#2f855a", "#d69e2e", "#c05621", "#718096", "#805ad5"];

const copyMap = {
  ar: {
    eyebrow: "تحليلات المدرب",
    title: "الرسوم وتحليل الأسئلة",
    subtitle: "عرض منظم للرسوم والأنماط العامة، مع كارت مستقل لكل سؤال تقييم رقمي على طريقة قراءة Google Forms.",
    course: "الكورس",
    mode: "نوع العرض",
    pre: "قبلي",
    post: "بعدي",
    both: "الاثنان معًا",
    chooseCourse: "اختر كورسًا",
    loading: "جارٍ تحميل التحليلات...",
    noCourses: "لا توجد كورسات بعد.",
    noData: "لا توجد بيانات كافية لهذا الجزء حتى الآن.",
    loadError: "تعذر تحميل التحليلات الآن. تأكد من تشغيل الباك ثم أعد المحاولة.",
    ageChart: "أعمار المتعلمين",
    governoratesChart: "المحافظات المشاركة",
    correctnessChart: "الصح والغلط",
    ageImprovement: "التحسن حسب الفئة العمرية",
    governorateImprovement: "التحسن حسب المحافظة",
    ratingQuestions: "أسئلة التقييم الرقمي",
    averageRating: "متوسط التقييم",
    answersCount: "عدد الإجابات",
    assessmentType: "نوع التقييم",
    noRatingQuestions: "لا توجد أسئلة تقييم رقمي في هذا العرض.",
    questionScale: "توزيع الإجابات من 0 إلى 10"
  },
  en: {
    eyebrow: "Trainer Analytics",
    title: "Charts And Question Analysis",
    subtitle: "A cleaner analytics view with one dedicated chart card per rating question, inspired by Google Forms summary reading patterns.",
    course: "Course",
    mode: "View Mode",
    pre: "Pre",
    post: "Post",
    both: "Both",
    chooseCourse: "Choose a course",
    loading: "Loading analytics...",
    noCourses: "There are no courses yet.",
    noData: "There is not enough data for this section yet.",
    loadError: "Unable to load analytics right now. Make sure the backend is running and try again.",
    ageChart: "Learner Ages",
    governoratesChart: "Participating Governorates",
    correctnessChart: "Correct vs Incorrect",
    ageImprovement: "Improvement By Age Group",
    governorateImprovement: "Improvement By Governorate",
    ratingQuestions: "Rating Questions",
    averageRating: "Average Rating",
    answersCount: "Answers",
    assessmentType: "Assessment Type",
    noRatingQuestions: "There are no rating questions in this view.",
    questionScale: "Answer Distribution From 0 To 10"
  }
};

function TrainerAnalyticsPage() {
  const user = getStoredUser();
  const { language } = useLanguage();
  const isArabic = language === "ar";
  const copy = repairMojibakeDeep(copyMap[isArabic ? "ar" : "en"]);

  const [courses, setCourses] = useState([]);
  const [selectedCourseId, setSelectedCourseId] = useState("");
  const [selectedMode, setSelectedMode] = useState("both");
  const [dashboardData, setDashboardData] = useState(null);
  const [isLoadingCourses, setIsLoadingCourses] = useState(true);
  const [isLoadingDashboard, setIsLoadingDashboard] = useState(false);
  const [pageError, setPageError] = useState("");

  useEffect(() => {
    let active = true;

    async function fetchCourses() {
      if (!user?.id) {
        setIsLoadingCourses(false);
        return;
      }

      try {
        setPageError("");
        const response = await api.get(`/trainer/${user.id}/courses`);
        if (!active) {
          return;
        }
        const nextCourses = repairMojibakeDeep(response.data.courses || []);
        setCourses(nextCourses);
        if (nextCourses.length) {
          setSelectedCourseId(String(nextCourses[0].id));
        }
      } catch (error) {
        if (!active) {
          return;
        }
        setCourses([]);
        setPageError(error.response?.data?.error || copy.loadError);
      } finally {
        if (active) {
          setIsLoadingCourses(false);
        }
      }
    }

    fetchCourses();
    return () => {
      active = false;
    };
  }, [copy.loadError, user?.id]);

  useEffect(() => {
    let active = true;

    async function fetchDashboard() {
      if (!user?.id || !selectedCourseId) {
        setDashboardData(null);
        return;
      }

      try {
        setIsLoadingDashboard(true);
        setPageError("");
        const response = await api.get(`/courses/${selectedCourseId}/dashboard-overview`, {
          params: {
            trainer_id: user.id,
            mode: selectedMode
          }
        });
        if (active) {
          setDashboardData(repairMojibakeDeep(response.data));
        }
      } catch (error) {
        if (!active) {
          return;
        }
        setDashboardData(null);
        setPageError(error.response?.data?.error || copy.loadError);
      } finally {
        if (active) {
          setIsLoadingDashboard(false);
        }
      }
    }

    fetchDashboard();
    return () => {
      active = false;
    };
  }, [copy.loadError, selectedCourseId, selectedMode, user?.id]);

  const correctnessChartData = useMemo(() => {
    const distribution = dashboardData?.correctness_distribution || [];
    const labels = isArabic ? { correct: "صح", incorrect: "غلط" } : { correct: "Correct", incorrect: "Incorrect" };
    return distribution.map((item) => ({
      name: item.name === "correct" ? labels.correct : labels.incorrect,
      value: item.value
    }));
  }, [dashboardData?.correctness_distribution, isArabic]);

  const ratingQuestions = useMemo(() => {
    return (dashboardData?.question_summaries || []).filter((question) => question.question_type === "rating");
  }, [dashboardData?.question_summaries]);

  const renderPieChart = (data, height = 280) => (
    data?.length ? (
      <ResponsiveContainer width="100%" height={height}>
        <PieChart>
          <Pie data={data} dataKey="value" outerRadius={90} label>
            {data.map((entry, index) => (
              <Cell key={`${entry.name}-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
            ))}
          </Pie>
          <Tooltip />
        </PieChart>
      </ResponsiveContainer>
    ) : (
      <div className="empty-panel compact-panel state-panel state-panel--empty">
        <p>{copy.noData}</p>
      </div>
    )
  );

  return (
    <div className="workspace-page">
      <section className="hero-panel trainer-overview-hero">
        <div>
          <p className="section-eyebrow">{copy.eyebrow}</p>
          <h2 className="section-title">{copy.title}</h2>
          <p className="section-subtitle">{copy.subtitle}</p>
        </div>
      </section>

      <section className="panel-card">
        <div className="two-column-grid">
          <label className="field-block">
            <span>{copy.course}</span>
            <select
              className="text-input"
              value={selectedCourseId}
              onChange={(event) => setSelectedCourseId(event.target.value)}
            >
              <option value="">{copy.chooseCourse}</option>
              {courses.map((course) => (
                <option key={course.id} value={course.id}>
                  {course.title}
                </option>
              ))}
            </select>
          </label>

          <label className="field-block">
            <span>{copy.mode}</span>
            <select
              className="text-input"
              value={selectedMode}
              onChange={(event) => setSelectedMode(event.target.value)}
            >
              <option value="pre">{copy.pre}</option>
              <option value="post">{copy.post}</option>
              <option value="both">{copy.both}</option>
            </select>
          </label>
        </div>

        {isLoadingCourses && <div className="empty-panel compact-panel state-panel state-panel--loading"><p>{copy.loading}</p></div>}
        {!isLoadingCourses && !courses.length && <div className="empty-panel compact-panel state-panel state-panel--empty"><p>{copy.noCourses}</p></div>}
        {!!pageError && <div className="empty-panel compact-panel state-panel state-panel--error"><p>{pageError}</p></div>}
      </section>

      <section className="panel-card">
        <h3>{copy.ageChart}</h3>
        {dashboardData?.age_distribution?.length ? (
          <div className="chart-panel">
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={dashboardData.age_distribution}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="value" fill="#2b6c8f" radius={[10, 10, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <p className="muted-text">{copy.noData}</p>
        )}
      </section>

      <section className="panel-card">
        <h3>{copy.governoratesChart}</h3>
        <div className="chart-panel">{renderPieChart(dashboardData?.governorates_distribution || [])}</div>
      </section>

      <section className="panel-card">
        <h3>{copy.correctnessChart}</h3>
        <div className="chart-panel">{renderPieChart(correctnessChartData)}</div>
      </section>

      <section className="panel-card">
        <h3>{copy.ageImprovement}</h3>
        {(dashboardData?.improvement_by_age || []).length ? (
          <div className="chart-panel">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={dashboardData.improvement_by_age}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="average_gain" fill="#1f7a8c" radius={[10, 10, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <p className="muted-text">{copy.noData}</p>
        )}
      </section>

      <section className="panel-card">
        <h3>{copy.governorateImprovement}</h3>
        {(dashboardData?.improvement_by_governorate || []).length ? (
          <div className="chart-panel">
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={dashboardData.improvement_by_governorate}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" interval={0} angle={-18} textAnchor="end" height={72} />
                <YAxis />
                <Tooltip />
                <Bar dataKey="average_gain" fill="#1f7a8c" radius={[10, 10, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <p className="muted-text">{copy.noData}</p>
        )}
      </section>

      <section className="panel-card">
        <h3>{copy.ratingQuestions}</h3>
        {isLoadingDashboard ? (
          <div className="empty-panel compact-panel state-panel state-panel--loading">
            <p>{copy.loading}</p>
          </div>
        ) : ratingQuestions.length ? (
          <div className="course-stack">
            {ratingQuestions.map((question) => (
              <article key={question.question_id} className="course-card trainer-rating-card">
                <div className="card-title-row">
                  <strong>{question.question_text}</strong>
                  <span className="status-pill">{question.assessment_type}</span>
                </div>
                <p className="muted-text">
                  {copy.assessmentType}: {question.assessment_title} - {copy.answersCount}: {question.total_answers} - {copy.averageRating}: {question.average_rating ?? "-"}
                </p>
                <p className="muted-text">{copy.questionScale}</p>
                <div className="chart-panel">
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart
                      data={(question.option_breakdown || [])
                        .map((item) => ({ name: String(item.label), value: item.value }))
                        .sort((a, b) => Number(a.name) - Number(b.name))}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis allowDecimals={false} />
                      <Tooltip />
                      <Bar dataKey="value" fill="#2f855a" radius={[10, 10, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <p className="muted-text">{copy.noRatingQuestions}</p>
        )}
      </section>
    </div>
  );
}

export default TrainerAnalyticsPage;
